interface LexdexLogoProps {
  className?: string;
  width?: number;
  height?: number;
  showText?: boolean;
}

export function LexdexLogo({ 
  className = "", 
  width = 120, 
  height = 140,
  showText = false 
}: LexdexLogoProps) {
  // Scale factor to fit different sizes
  const iconViewBox = "277 120 470 546"; // Hexagon + LD from original SVG
  const fullViewBox = "145 120 730 726"; // Full logo with text
  
  return (
    <svg 
      width={width} 
      height={showText ? height * 1.4 : height} 
      viewBox={showText ? fullViewBox : iconViewBox} 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id="lexdex-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8B5CF6" />
          <stop offset="50%" stopColor="#6366F1" />
          <stop offset="100%" stopColor="#3B82F6" />
        </linearGradient>
      </defs>
      
      {/* Background matching header */}
      <rect 
        x={showText ? "145" : "277"} 
        y="120" 
        width={showText ? "730" : "470"} 
        height={showText ? "726" : "546"} 
        fill="hsl(0, 0%, 16%)" 
      />
      
      {/* Hexagon Border with gradient stroke - clean uniform hexagon */}
      <path 
        stroke="url(#lexdex-gradient)" 
        strokeWidth="24" 
        fill="none" 
        strokeLinejoin="round"
        strokeLinecap="round"
        d="M 512 130 L 730 265 L 730 510 L 512 645 L 294 510 L 294 265 Z"
      />
      
      {/* Inner hexagon background matching header */}
      <path 
        fill="hsl(0, 0%, 16%)" 
        d="M 511.88 149.34 Q 512.50 149.34 513.65 150.02 Q 572.45 184.48 719.54 270.56 A 2.57 2.56 -74.9 0 1 720.81 272.77 Q 720.89 305.06 720.93 387.94 Q 720.97 470.81 720.91 503.10 A 2.57 2.56 74.9 0 1 719.64 505.31 Q 572.63 591.52 513.86 626.03 Q 512.71 626.71 512.08 626.71 Q 511.46 626.71 510.31 626.04 Q 451.51 591.57 304.42 505.49 A 2.57 2.56 -74.9 0 1 303.15 503.29 Q 303.07 471.00 303.03 388.12 Q 303.00 305.24 303.05 272.95 A 2.57 2.56 74.9 0 1 304.32 270.74 Q 451.33 184.54 510.10 150.02 Q 511.26 149.34 511.88 149.34 Z"
      />
      
      {/* "L" Shape - Filled */}
      <path 
        fill="url(#lexdex-gradient)" 
        d="M 431.49 252.06 L 432.07 252.63 L 432.07 470.15 A 0.48 0.48 0 0 0 432.55 470.63 Q 503.92 470.58 517.25 470.52 C 525.77 470.48 532.54 470.56 540.18 466.15 C 548.11 461.57 553.32 454.95 558.09 446.71 A 1.26 1.24 15.0 0 1 559.17 446.10 L 562.76 446.10 A 0.57 0.56 -0.0 0 1 563.33 446.66 L 563.33 518.11 A 0.25 0.25 0.0 0 1 563.08 518.36 L 500.03 518.71 L 374.94 519.01 A 1.02 1.02 0.0 0 1 373.92 517.99 L 373.92 512.85 A 1.75 1.75 0.0 0 1 374.86 511.30 Q 389.15 503.93 389.84 487.15 Q 390.21 478.44 390.15 466.25 Q 389.88 410.53 389.98 354.78 L 389.85 280.43 Q 388.05 265.71 375.99 257.82 Q 374.09 256.59 373.93 255.48 Q 373.71 254.05 374.06 252.66 A 0.79 0.79 0.0 0 1 374.83 252.06 L 431.49 252.06 Z"
      />
      
      {/* "D" Shape - Filled */}
      <path 
        fill="url(#lexdex-gradient)" 
        d="M 465.26 285.63 Q 467.48 285.43 470.01 285.44 Q 521.36 285.49 539.64 285.32 Q 556.15 285.17 564.27 286.90 Q 583.25 290.95 597.97 305.27 C 623.07 329.67 627.08 366.80 625.84 399.43 C 624.64 431.00 613.62 471.46 580.24 485.10 A 1.89 1.89 0.0 0 0 579.07 486.85 L 579.07 516.73 A 0.48 0.48 0.0 0 0 579.56 517.21 Q 583.83 517.08 588.52 515.79 C 613.76 508.83 633.96 491.72 647.12 469.11 C 661.31 444.74 666.30 416.78 666.67 389.26 Q 666.79 380.41 665.48 367.76 C 661.92 333.13 647.84 301.11 621.45 278.46 C 610.12 268.74 597.80 262.31 583.07 257.97 Q 567.56 253.40 553.38 252.17 Q 550.20 251.89 523.13 252.00 Q 510.78 252.06 465.21 252.07 A 0.31 0.31 0.0 0 0 464.90 252.38 L 464.90 285.31 A 0.32 0.32 0.0 0 0 465.26 285.63 Z"
      />
      
      {/* "LexDex" Text (only shown when showText is true) */}
      {showText && (
        <>
          {/* "L" */}
          <path 
            fill="url(#lexdex-gradient)" 
            d="M 146.44 844.12 A 1.58 1.56 -16.0 0 0 147.77 844.85 L 238.99 844.78 A 0.96 0.95 90.0 0 0 239.94 843.82 L 239.94 821.60 A 0.72 0.71 -90.0 0 0 239.23 820.88 Q 199.24 820.85 174.53 820.93 Q 172.03 820.93 171.93 818.46 Q 171.28 803.04 171.68 771.07 Q 171.69 770.68 171.49 767.85 A 4.95 4.47 49.2 0 1 171.51 766.98 Q 171.86 763.73 171.42 761.17 A 3.33 3.31 46.9 0 1 171.46 759.82 Q 172.02 757.52 171.88 755.99 Q 171.37 750.01 171.74 744.75 Q 171.92 742.26 171.86 739.74 Q 171.77 736.23 171.79 734.69 Q 172.10 708.71 171.84 692.43 A 0.81 0.80 -0.4 0 0 171.03 691.64 L 146.72 691.64 A 0.98 0.98 0.0 0 0 145.74 692.64 Q 146.18 712.34 146.08 731.50 Q 145.74 797.16 145.89 798.46 Q 145.90 798.53 146.08 819.54 Q 146.16 829.48 145.92 840.77 Q 145.86 843.22 146.44 844.12 Z"
          />
          {/* "e" */}
          <path 
            fill="url(#lexdex-gradient)" 
            d="M 278.56 798.21 Q 278.48 797.36 279.30 797.52 L 361.22 797.57 A 0.65 0.64 -87.0 0 0 361.86 796.99 Q 362.30 792.54 362.33 785.71 C 362.42 766.41 353.82 748.24 337.42 738.09 C 320.39 727.55 298.47 727.79 280.77 737.18 C 265.50 745.27 255.69 759.99 252.62 776.85 C 244.77 819.94 273.80 850.86 317.21 848.39 Q 336.27 847.31 350.62 836.12 Q 354.48 833.11 357.58 830.51 A 0.27 0.26 50.2 0 0 357.61 830.13 L 342.25 812.39 A 0.61 0.61 0.0 0 0 341.30 812.43 Q 332.55 824.44 317.88 826.10 C 298.19 828.33 281.15 818.78 278.56 798.21 Z"
          />
          {/* "x" */}
          <path 
            fill="url(#lexdex-gradient)" 
            d="M 407.49 785.76 Q 408.20 786.90 408.11 787.80 L 365.86 845.10 A 0.52 0.52 0.0 0 0 366.28 845.93 L 395.38 845.93 A 2.17 2.17 0.0 0 0 397.13 845.04 Q 405.42 833.76 413.64 822.15 Q 419.94 813.26 423.20 806.72 Q 423.16 806.42 423.48 806.30 Q 423.73 806.21 423.99 806.34 Q 424.46 806.55 424.69 807.10 L 450.98 844.72 A 2.89 2.88 -17.4 0 0 453.35 845.96 L 482.39 845.96 A 0.38 0.38 0.0 0 0 482.70 845.36 L 441.44 786.98 A 1.03 1.01 -45.3 0 1 441.44 785.81 L 479.39 732.52 A 0.07 0.07 0.0 0 0 479.33 732.41 L 450.67 732.41 A 2.17 2.16 -72.1 0 0 448.91 733.32 Q 437.13 750.00 426.25 768.44 Q 424.80 767.88 424.34 767.25 L 401.17 733.72 A 2.99 2.98 -17.3 0 0 398.71 732.43 L 369.14 732.43 A 0.21 0.21 0.0 0 0 368.97 732.77 L 407.49 785.76 Z"
          />
          {/* "D" */}
          <path 
            fill="url(#lexdex-gradient)" 
            d="M 499.56 691.00 Q 541.24 690.66 554.22 691.03 C 578.65 691.72 602.34 701.36 617.75 721.16 C 632.33 739.90 635.22 768.92 628.85 791.17 C 619.58 823.54 592.32 841.39 559.75 845.33 Q 553.74 846.06 541.26 845.99 Q 519.29 845.85 499.51 845.94 A 0.54 0.54 0.0 0 1 498.97 845.40 L 498.97 691.60 A 0.60 0.59 90.0 0 1 499.56 691.00 Z"
          />
          {/* "e" (second) */}
          <path 
            fill="url(#lexdex-gradient)" 
            d="M 735.54 812.45 Q 724.64 826.81 705.90 826.30 Q 685.90 825.75 677.00 810.05 C 674.71 806.01 673.89 802.80 673.08 798.04 A 0.35 0.35 0.0 0 1 673.43 797.63 L 755.44 797.63 A 0.41 0.41 0.0 0 0 755.85 797.25 Q 756.68 785.33 755.34 776.01 C 752.02 753.02 736.92 734.17 713.08 730.77 C 694.45 728.11 673.37 733.88 661.06 748.82 C 649.76 762.51 646.14 778.22 647.10 795.76 C 649.07 831.50 678.64 850.49 712.19 848.35 C 727.82 847.35 739.37 840.29 750.96 830.20 A 0.46 0.46 0.0 0 0 751.01 829.55 L 736.19 812.43 A 0.42 0.42 0.0 0 0 735.54 812.45 Z"
          />
          {/* "x" (second) */}
          <path 
            fill="url(#lexdex-gradient)" 
            d="M 815.06 807.72 Q 815.45 807.04 815.99 807.14 Q 816.29 807.20 816.39 807.57 L 842.11 844.40 A 3.72 3.72 0.0 0 0 845.16 845.99 L 874.39 845.99 A 0.32 0.32 0.0 0 0 874.65 845.49 L 833.47 789.01 A 1.83 1.82 44.9 0 1 833.46 786.87 L 872.01 732.72 A 0.27 0.26 -72.0 0 0 871.80 732.30 L 843.67 732.30 A 1.53 1.52 16.6 0 0 842.40 732.98 L 818.59 768.85 Q 818.29 769.37 817.52 768.99 L 792.99 733.00 A 1.52 1.51 -16.9 0 0 791.73 732.33 L 761.22 732.33 A 0.34 0.34 0.0 0 0 760.94 732.87 L 800.06 786.52 A 1.88 1.87 -44.3 0 1 800.03 788.76 C 785.76 807.37 771.51 826.01 758.80 845.45 A 0.33 0.33 0.0 0 0 759.08 845.96 Q 766.63 845.87 785.51 846.09 Q 788.16 846.11 788.91 845.17 Q 794.82 837.75 810.80 815.05 Q 812.39 812.79 815.06 807.72 Z"
          />
        </>
      )}
    </svg>
  );
}
